import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { GridPageChangeEvent, GridPagerSettings, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { orderBy, process, SortDescriptor } from '@progress/kendo-data-query'

import { SenderPolicy, SenderPolicyStatus } from 'types/Settings'

const BLANK_ENTRY = { name: '', policy: SenderPolicyStatus.block, comment: '', id: '' }

export interface SenderPoliciesPaginationArgs {
  data: SenderPolicy[]
}

export const useSenderPoliciesPagination = ({ data }: SenderPoliciesPaginationArgs) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sort, setSort] = useState<SortDescriptor[]>([{ field: 'modified', dir: 'desc' }])
  const [searchKeyword, setSearchKeyword] = useState('')

  const paginationSettings: GridPagerSettings = useMemo(
    () => ({
      pageSizes: [10, 20, 50, 100, 200]
    }),
    []
  )

  const onPageChange = useCallback((evt: GridPageChangeEvent) => {
    const {
      page: { skip, take }
    } = evt
    const nextPage = skip / take + 1
    const nextPageSize = take
    setCurrentPage(nextPage)
    setPageSize(nextPageSize)
  }, [])

  const onSortChange = useCallback((evt: GridSortChangeEvent) => {
    setSort(evt.sort)
  }, [])

  const onSearchKeywordChange = useCallback((evt: ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(evt.target.value)
  }, [])

  const onClearSearchKeyword = useCallback(() => {
    setSearchKeyword('')
  }, [])

  const filteredData = useMemo(() => {
    const { data: processedData } = process(data, {
      filter: {
        logic: 'or',
        filters: [
          { field: 'name', operator: 'contains', value: searchKeyword },
          { field: 'comment', operator: 'contains', value: searchKeyword }
        ]
      }
    })
    return processedData
  }, [data, searchKeyword])

  const visibleData = useMemo(() => {
    const orderedData = orderBy(filteredData, sort)
    const { data: processedData } = process(orderedData, {
      skip: (currentPage - 1) * pageSize,
      take: pageSize,
      sort
    })
    processedData.unshift(BLANK_ENTRY)
    return processedData
  }, [currentPage, filteredData, pageSize, sort])

  return useMemo(
    () => ({
      paginationSettings,
      pageSize,
      currentPage,
      visibleData,
      data,
      total: filteredData.length,
      sort,
      searchKeyword,
      onPageChange,
      onSortChange,
      onSearchKeywordChange,
      onClearSearchKeyword
    }),
    [
      paginationSettings,
      pageSize,
      currentPage,
      visibleData,
      data,
      filteredData.length,
      sort,
      searchKeyword,
      onPageChange,
      onSortChange,
      onSearchKeywordChange,
      onClearSearchKeyword
    ]
  )
}

export type SenderPoliciesPagination = ReturnType<typeof useSenderPoliciesPagination>
